import React, { useState, useEffect, useMemo } from 'react';
import { ArrowPathIcon, UserPlusIcon, FunnelIcon, XMarkIcon, MagnifyingGlassIcon, UsersIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    fetchCRMUsers,
    updateUserStage,
    selectAllUsers,
    CRM_STAGES,
    STAGE_CONFIG,
    updateUserLocal,
    selectTodayAppointmentsCount,
    fetchAndSyncFromExternalAPI,
    createNewUser,
} from '../../redux/slices/crmSlice';
import { subscribeToUsers } from '../../services/crmService';
import UserCard from '../../components/crm/UserCard';
import AddUserModal from '../../components/crm/AddUserModal';
import AppointmentModal from '../../components/crm/AppointmentModal';
import SelectUserModal from '../../components/crm/SelectUserModal';
import { createPortal } from 'react-dom';
import CustomersSidebar from '../../components/crm/CustomersSidebar';
import toast from 'react-hot-toast';
import { CalendarDaysIcon, ChartBarIcon, ViewColumnsIcon } from '@heroicons/react/24/outline';
import DashboardMetrics from '../../components/dashboard/DashboardMetrics';
import ShakeQuantityMonitor from '../../components/analytics/ShakeQuantityMonitor';
import ReferralAnalytics from '../../components/analytics/ReferralAnalytics';

const AdminCrm = () => {
    return (
        <div className='min-h-screen bg-slate-50'>
            <Board />
        </div>
    );
};

const Board = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const allUsers = useSelector(selectAllUsers);
    const todayAppointmentsCount = useSelector(selectTodayAppointmentsCount);
    const [syncing, setSyncing] = useState(false);
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showSelectUserModal, setShowSelectUserModal] = useState(false);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [selectedUserForAppointment, setSelectedUserForAppointment] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showCustomersSidebar, setShowCustomersSidebar] = useState(false);
    const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
    const [activeView, setActiveView] = useState('kanban'); // 'kanban' or 'analytics'

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedLeadTypes, setSelectedLeadTypes] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Available services list
    const availableServices = [
        'Weight Gain',
        'Weight Loss',
        'Skin Care',
        'Products',
        'Child Nutrition',
        'General Health'
    ];

    // Filter users based on all criteria
    const users = useMemo(() => {
        let filtered = [...allUsers];

        // Search by name or mobile number
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(user =>
                user.name?.toLowerCase().includes(query) ||
                user.number?.toLowerCase().includes(query)
            );
        }

        // Filter by services
        if (selectedServices.length > 0) {
            filtered = filtered.filter(user => {
                if (!user.services || user.services.length === 0) return false;
                const userServices = Array.isArray(user.services) ? user.services : [user.services];
                return userServices.some(service => selectedServices.includes(service));
            });
        }

        // Filter by lead type
        if (selectedLeadTypes.length > 0) {
            filtered = filtered.filter(user => {
                const leadType = user.leadType || 'service';
                return selectedLeadTypes.includes(leadType);
            });
        }

        // Filter by date range
        if (dateFrom) {
            filtered = filtered.filter(user => {
                const userDate = user.createdAt || user.api_created_at || user.created_at;
                if (!userDate) return false;
                const date = new Date(userDate);
                return date >= new Date(dateFrom);
            });
        }

        if (dateTo) {
            filtered = filtered.filter(user => {
                const userDate = user.createdAt || user.api_created_at || user.created_at;
                if (!userDate) return false;
                const date = new Date(userDate);
                return date <= new Date(dateTo + 'T23:59:59');
            });
        }

        return filtered;
    }, [allUsers, searchQuery, selectedServices, selectedLeadTypes, dateFrom, dateTo]);

    // Check if any filters are active
    const hasActiveFilters = searchQuery || selectedServices.length > 0 ||
        selectedLeadTypes.length > 0 || dateFrom || dateTo;

    // Clear all filters
    const clearAllFilters = () => {
        setSearchQuery('');
        setSelectedServices([]);
        setSelectedLeadTypes([]);
        setDateFrom('');
        setDateTo('');
    };

    // Toggle service selection
    const toggleService = (service) => {
        setSelectedServices(prev =>
            prev.includes(service)
                ? prev.filter(s => s !== service)
                : [...prev, service]
        );
    };

    // Toggle lead type selection
    const toggleLeadType = (type) => {
        setSelectedLeadTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    // Fetch users and API leads on mount
    useEffect(() => {
        // Fetch existing users from Firebase
        dispatch(fetchCRMUsers());

        // Fetch leads from external API
        dispatch(fetchAndSyncFromExternalAPI());
    }, [dispatch]);

    // Subscribe to real-time updates
    useEffect(() => {
        const unsubscribe = subscribeToUsers((updatedUsers) => {
            updatedUsers.forEach((user) => {
                dispatch(updateUserLocal(user));
            });
        });

        return () => unsubscribe();
    }, [dispatch]);

    // Handle sync from external API
    const handleSync = async () => {
        setSyncing(true);
        toast.loading('Fetching leads from external API...', { id: 'sync' });

        try {
            // Fetch leads from external API (they will be added to Redux state)
            // They will only be saved to Firebase when their stage is changed
            const result = await dispatch(fetchAndSyncFromExternalAPI()).unwrap();

            toast.success(
                `${result.length} leads fetched successfully! Change their stage to save to Firebase.`,
                { id: 'sync' },
            );
        } catch (error) {
            console.error('Sync error:', error);
            toast.error('Failed to fetch leads: ' + (error || 'Unknown error'), { id: 'sync' });
        } finally {
            setSyncing(false);
        }
    };

    // Handle adding new user
    const handleAddUser = async (userData) => {
        toast.loading('Adding new user...', { id: 'add-user' });

        try {
            await dispatch(createNewUser(userData)).unwrap();
            toast.success('User added successfully!', { id: 'add-user' });
        } catch (error) {
            console.error('Error adding user:', error);
            toast.error('Failed to add user: ' + (error || 'Unknown error'), { id: 'add-user' });
        }
    };

    return (
        <div className='px-6 py-6'>
            {/* Header */}
            <div className='bg-white rounded-2xl shadow-sm border border-slate-100  mb-6'>
                <div className='p-4 border-b border-slate-100 bg-slate-50'>
                    <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                        CRM Dashboard
                    </h3>
                </div>

                <div className='p-4'>
                    <div className='flex flex-col md:flex-row items-start md:items-center justify-between gap-4'>
                        <div>
                            <h1 className='text-xl font-black text-slate-900'>Lead Management</h1>
                            <p className='text-sm text-slate-500 font-medium mt-1'>
                                Manage your leads and customers
                            </p>
                        </div>

                        <div className='flex flex-wrap items-center gap-3 w-full md:w-auto'>
                            {/* View Toggle */}
                            <div className='flex items-center gap-2 p-1 bg-slate-100 rounded-xl'>
                                <button
                                    onClick={() => setActiveView('kanban')}
                                    className={`px-3 py-2 rounded-lg font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 ${activeView === 'kanban'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                >
                                    <ViewColumnsIcon className='h-4 w-4' />
                                    <span className='hidden md:inline'>Kanban</span>
                                </button>
                                <button
                                    onClick={() => setActiveView('analytics')}
                                    className={`px-3 py-2 rounded-lg font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 ${activeView === 'analytics'
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                >
                                    <ChartBarIcon className='h-4 w-4' />
                                    <span className='hidden md:inline'>Analytics</span>
                                </button>
                            </div>

                            {/* Today's Appointments */}
                            <div className='flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100'>
                                <CalendarDaysIcon className='h-5 w-5 text-blue-600 flex-shrink-0' />
                                <div>
                                    <p className='text-xl font-black text-blue-700'>
                                        {todayAppointmentsCount}
                                    </p>
                                    <p className='text-[9px] font-black text-blue-600 uppercase tracking-widest'>
                                        Today's Appointments
                                    </p>
                                </div>
                            </div>

                            {/* Customers Button */}
                            <button
                                onClick={() => navigate('/admin/crm-users')}
                                className='px-4 py-3 bg-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-purple-700 transition-all shadow-sm flex items-center gap-2'
                            >
                                <UsersIcon className='h-4 w-4' />
                                <span className='hidden md:inline'>Customers</span>
                            </button>

                            <div className='flex items-center gap-3'>
                            <button
                                onClick={() => setShowAddUserModal(true)}
                                className='px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all flex items-center gap-2 text-sm font-black uppercase tracking-widest shadow-lg'
                            >
                                <UserPlusIcon className='h-5 w-5' />
                                Add User
                            </button>
                            <button
                                onClick={() => {
                                    if (allUsers.length > 0) {
                                        setShowSelectUserModal(true);
                                    } else {
                                        toast.error('No users available. Please add a user first.');
                                    }
                                }}
                                className='px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all flex items-center gap-2 text-sm font-black uppercase tracking-widest shadow-lg'
                            >
                                <CalendarDaysIcon className='h-5 w-5' />
                                Schedule
                            </button>
                            <button
                                onClick={handleSync}
                                disabled={syncing}
                                className='px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-all flex items-center gap-2 text-sm font-black uppercase tracking-widest disabled:opacity-50'
                            >
                                <ArrowPathIcon className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
                                {syncing ? 'Syncing...' : 'Sync Leads'}
                            </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter Section */}
            <div className='bg-white rounded-2xl shadow-sm border border-slate-100  mb-6'>
                <div className='p-4 border-b border-slate-100 bg-slate-50'>
                    <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                        Search & Filters
                    </h3>
                </div>

                <div className='p-4 space-y-4'>
                    {/* Search Bar and Filter Toggle */}
                    <div className='flex gap-3'>
                        {/* Search Input */}
                        <div className='flex-1 relative'>
                            <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400' />
                            <input
                                type='text'
                                placeholder='Search by name or mobile number...'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className='w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                            />
                        </div>

                        {/* Filter Toggle Button */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-2.5 rounded-xl border font-black text-xs uppercase tracking-widest transition-all flex items-center gap-2 ${showFilters || hasActiveFilters
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                }`}
                        >
                            <FunnelIcon className='h-4 w-4' />
                            <span className='hidden md:inline'>Filters</span>
                            {hasActiveFilters && (
                                <span className='bg-white text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-black'>
                                    {[searchQuery, selectedServices.length > 0, selectedLeadTypes.length > 0, dateFrom, dateTo].filter(Boolean).length}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className='bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-4'>
                            <div className='flex items-center justify-between'>
                                <h4 className='text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                    Advanced Filters
                                </h4>
                                {hasActiveFilters && (
                                    <button
                                        onClick={clearAllFilters}
                                        className='flex items-center gap-1 text-[9px] text-red-600 hover:text-red-700 font-black uppercase tracking-widest'
                                    >
                                        <XMarkIcon className='h-3 w-3' />
                                        Clear All
                                    </button>
                                )}
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                {/* Date Range Filter */}
                                <div className='space-y-2'>
                                    <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                        Date Range
                                    </label>
                                    <div className='grid grid-cols-2 gap-2'>
                                        <input
                                            type='date'
                                            value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            className='w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                                        />
                                        <input
                                            type='date'
                                            value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            className='w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                                        />
                                    </div>
                                </div>

                                {/* Lead Type Filter */}
                                <div className='space-y-2'>
                                    <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                        Lead Type
                                    </label>
                                    <div className='flex gap-2'>
                                        <button
                                            onClick={() => toggleLeadType('service')}
                                            className={`flex-1 px-3 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl border transition-all ${selectedLeadTypes.includes('service')
                                                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                        >
                                            Service
                                        </button>
                                        <button
                                            onClick={() => toggleLeadType('business')}
                                            className={`flex-1 px-3 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl border transition-all ${selectedLeadTypes.includes('business')
                                                    ? 'bg-purple-600 border-purple-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                        >
                                            Business
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Services Filter */}
                            <div className='space-y-2'>
                                <label className='block text-[9px] font-black text-slate-400 uppercase tracking-widest'>
                                    Services
                                </label>
                                <div className='flex flex-wrap gap-2'>
                                    {availableServices.map((service) => (
                                        <button
                                            key={service}
                                            onClick={() => toggleService(service)}
                                            className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl border transition-all ${selectedServices.includes(service)
                                                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg'
                                                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                        >
                                            {service}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Results Count */}
                            <div className='pt-3 border-t border-slate-200'>
                                <p className='text-sm font-semibold text-slate-900'>
                                    Showing {users.length} of {allUsers.length} leads
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Conditional Rendering based on Active View */}
            {activeView === 'kanban' ? (
                /* Kanban Board */
                <div className='grid grid-cols-1 md:grid-cols-6 gap-4'>
                    {Object.keys(CRM_STAGES).map((stageKey) => {
                        const stage = CRM_STAGES[stageKey];
                        const config = STAGE_CONFIG[stage];

                        return (
                            <Column
                                key={stage}
                                title={config.title}
                                column={stage}
                                headingColor={config.color}
                                description={config.description}
                                users={users}
                            />
                        );
                    })}
                </div>
            ) : (
                /* Analytics View */
                <div className='space-y-6'>
                    {/* Dashboard Metrics */}
                    <div className='bg-white rounded-2xl shadow-sm border border-slate-100 '>
                        <div className='p-4 border-b border-slate-100 bg-slate-50'>
                            <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                                Performance Metrics
                            </h3>
                        </div>
                        <div className='p-6'>
                            <DashboardMetrics users={allUsers} />
                        </div>
                    </div>

                    {/* Shake Quantity Monitor */}
                    <div className='bg-white rounded-2xl shadow-sm border border-slate-100 '>
                        <div className='p-4 border-b border-slate-100 bg-slate-50'>
                            <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                                Shake Quantity Intelligence
                            </h3>
                        </div>
                        <div className='p-6'>
                            <ShakeQuantityMonitor users={allUsers} />
                        </div>
                    </div>

                    {/* Referral Analytics */}
                    <div className='bg-white rounded-2xl shadow-sm border border-slate-100 '>
                        <div className='p-4 border-b border-slate-100 bg-slate-50'>
                            <h3 className='text-[10px] font-black text-slate-400 uppercase tracking-widest'>
                                Referral Network
                            </h3>
                        </div>
                        <div className='p-6'>
                            <ReferralAnalytics users={allUsers} />
                        </div>
                    </div>
                </div>
            )}

            {/* Customers Sidebar */}
            <CustomersSidebar
                isOpen={showCustomersSidebar}
                onClose={() => setShowCustomersSidebar(false)}
                users={allUsers}
                searchQuery={sidebarSearchQuery}
                onSearchChange={setSidebarSearchQuery}
            />

            {/* Add User Modal */}
            <AddUserModal
                isOpen={showAddUserModal}
                onClose={() => setShowAddUserModal(false)}
                onSubmit={handleAddUser}
            />

            {/* Select User Modal - Rendered via Portal */}
            {createPortal(
                <SelectUserModal
                    isOpen={showSelectUserModal}
                    onClose={() => setShowSelectUserModal(false)}
                    users={allUsers}
                    onSelectUser={(user) => {
                        setSelectedUserForAppointment(user);
                        setShowAppointmentModal(true);
                    }}
                />,
                document.body
            )}

            {/* Appointment Modal - Rendered via Portal */}
            {selectedUserForAppointment && createPortal(
                <AppointmentModal
                    isOpen={showAppointmentModal}
                    onClose={() => {
                        setShowAppointmentModal(false);
                        setSelectedUserForAppointment(null);
                    }}
                    user={selectedUserForAppointment}
                    editingAppointment={null}
                />,
                document.body
            )}
        </div>
    );
};

const Column = ({ title, headingColor, description, column, users }) => {
    const dispatch = useDispatch();
    const [active, setActive] = useState(false);

    const handleDragStart = (e, user) => {
        e.dataTransfer.setData('userId', user.id);
    };

    const handleDragEnd = async (e) => {
        const userId = e.dataTransfer.getData('userId');

        setActive(false);
        clearHighlights();

        if (!userId) return;

        // Update in Firebase
        try {
            await dispatch(updateUserStage({ userId, newStage: column })).unwrap();
            toast.success('User stage updated!');
        } catch (error) {
            console.error('Error updating stage:', error);
            toast.error('Failed to update stage');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        highlightIndicator(e);
        setActive(true);
    };

    const clearHighlights = (els) => {
        const indicators = els || getIndicators();
        indicators.forEach((i) => {
            i.style.opacity = '0';
        });
    };

    const highlightIndicator = (e) => {
        const indicators = getIndicators();
        clearHighlights(indicators);
        const el = getNearestIndicator(e, indicators);
        el.element.style.opacity = '1';
    };

    const getNearestIndicator = (e, indicators) => {
        const DISTANCE_OFFSET = 50;

        const el = indicators.reduce(
            (closest, child) => {
                const box = child.getBoundingClientRect();
                const offset = e.clientY - (box.top + DISTANCE_OFFSET);

                if (offset < 0 && offset > closest.offset) {
                    return { offset: offset, element: child };
                } else {
                    return closest;
                }
            },
            {
                offset: Number.NEGATIVE_INFINITY,
                element: indicators[indicators.length - 1],
            },
        );

        return el;
    };

    const getIndicators = () => {
        return Array.from(document.querySelectorAll(`[data-column="${column}"]`));
    };

    const handleDragLeave = () => {
        clearHighlights();
        setActive(false);
    };

    const filteredUsers = users.filter((u) => u.stage === column);

    return (
        <div className='w-full'>
            <div className='mb-3 rounded-2xl bg-white border border-gray-100 p-3 md:p-4 shadow-sm'>
                <div className='flex items-center justify-between mb-1'>
                    <h3 className={`font-semibold text-[13px] md:text-lg ${headingColor}`}>{title}</h3>
                    <span className='rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] md:text-xs font-medium text-gray-700'>
                        {filteredUsers.length}
                    </span>
                </div>
                <p className='text-[11px] md:text-xs text-gray-500'>{description}</p>
            </div>
            <div
                onDrop={handleDragEnd}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`h-full rounded-xl transition-colors p-2 ${active ? 'bg-blue-50 border-2 border-dashed border-blue-300' : 'bg-transparent'
                    }`}
            >
                <div className='space-y-2 md:space-y-3'>
                    {filteredUsers.map((user) => {
                        return (
                            <div key={user.id}>
                                <DropIndicator beforeId={user.id} column={column} />
                                <UserCard user={user} handleDragStart={handleDragStart} />
                            </div>
                        );
                    })}
                    <DropIndicator beforeId={null} column={column} />
                </div>
            </div>
        </div>
    );
};

const DropIndicator = ({ beforeId, column }) => {
    return (
        <div
            data-before={beforeId || '-1'}
            data-column={column}
            className='my-0.5 h-0.5 w-full bg-blue-400 opacity-0'
        />
    );
};

export default AdminCrm;
